<template>
  <div class="flex-1 overflow-auto p-6">
    <!-- QM §1.2 — the account feature flag gates the whole screen; the route itself is
         reachable by URL even while the sidebar entry is hidden. -->
    <p v-if="!isQueueManagementEnabled" class="text-slate-600">
      {{ $t('QUEUES_MGMT.DISABLED') }}
    </p>
    <!-- QM §5.4 — `Queues` read decides whether this screen exists for the viewer. -->
    <p v-else-if="!canViewQueues" class="text-slate-600">
      {{ $t('QUEUES_MGMT.FORBIDDEN') }}
    </p>
    <div v-else class="max-w-[60rem]">
      <div class="flex items-start justify-between mb-6">
        <div>
          <h1
            class="text-2xl font-medium text-slate-900 dark:text-slate-100 mb-1"
          >
            {{ $t('QUEUES_MGMT.AGENT.HEADER') }}
          </h1>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('QUEUES_MGMT.AGENT.SUB_TEXT') }}
          </p>
        </div>
        <woot-button
          v-if="hasQueueSupervision"
          variant="smooth"
          size="small"
          icon="people-team"
          @click="$router.push({ name: 'queues_supervisor' })"
        >
          {{ $t('QUEUES_MGMT.AGENT.SUPERVISOR_VIEW') }}
        </woot-button>
      </div>

      <!-- QM §5.7 — waiting counts stop updating when the push channel drops -->
      <queue-connection-banner @refresh="refreshCounts" />

      <woot-loading-state
        v-if="uiFlags.isFetching"
        :message="$t('QUEUES_MGMT.AGENT.LOADING')"
      />

      <p
        v-else-if="!myQueues.length"
        class="text-slate-600 dark:text-slate-400"
      >
        {{ $t('QUEUES_MGMT.AGENT.EMPTY') }}
      </p>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="queue in myQueues"
          :key="queue.id"
          class="flex items-center justify-between p-4 rounded-lg border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
        >
          <div>
            <p class="font-medium text-slate-900 dark:text-slate-100">
              {{ queue.name }}
            </p>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{
                $t('QUEUES_MGMT.AGENT.WAITING_COUNT', {
                  count: waitingCount(queue.id),
                })
              }}
            </p>
          </div>
          <!-- QM §5.4.3 — taking work is the `Queues` update action. -->
          <div v-if="canActOnQueues" class="flex items-center gap-2">
            <woot-button
              variant="smooth"
              size="small"
              icon="document-list-clock"
              @click="openBrowse(queue)"
            >
              {{ $t('QUEUES_MGMT.AGENT.BROWSE') }}
            </woot-button>
            <woot-button
              color-scheme="primary"
              size="small"
              icon="arrow-right"
              :is-loading="takingFrom === queue.id"
              :is-disabled="waitingCount(queue.id) === 0"
              @click="onTakeNext(queue)"
            >
              {{ $t('QUEUES_MGMT.AGENT.TAKE_NEXT') }}
            </woot-button>
          </div>
        </div>
      </div>

      <!-- QM §5.4.2 — the agent's own tickets -->
      <div class="mt-8">
        <my-tickets-list ref="myTickets" @returned="refreshCounts" />
      </div>
    </div>

    <queue-browse-modal
      v-if="browseQueue"
      :show="showBrowse"
      :queue-id="browseQueue.id"
      :queue-name="browseQueue.name"
      @taken="onTicketTaken"
      @close="closeBrowse"
    />

    <!-- QM §5.4.3 override confirmation -->
    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('QUEUES_MGMT.AGENT.CONFIRM.TITLE')"
      :description="confirmMessage"
      :confirm-label="$t('QUEUES_MGMT.AGENT.CONFIRM.PROCEED')"
      :cancel-label="$t('QUEUES_MGMT.AGENT.CONFIRM.CANCEL')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';
import QueueBrowseModal from './QueueBrowseModal.vue';
import MyTicketsList from './MyTicketsList.vue';
import QueueConnectionBanner from './QueueConnectionBanner.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// QM §5.7 — tickets enter and leave a queue in bursts (a sweep drains up to 20 at once),
// so coalesce the resulting push events into one recount, well inside the 2s budget.
const RECOUNT_DEBOUNCE_MS = 500;

// QM §5.4.3, §4.14 — override flags the server returns for an unconfirmed "Take next".
const OVERRIDE_MESSAGES = {
  pulled_while_at_capacity: 'QUEUES_MGMT.AGENT.CONFIRM.AT_CAPACITY',
  pulled_while_busy: 'QUEUES_MGMT.AGENT.CONFIRM.BUSY',
  pulled_while_offline: 'QUEUES_MGMT.AGENT.CONFIRM.OFFLINE',
  pulled_while_excluded: 'QUEUES_MGMT.AGENT.CONFIRM.EXCLUDED',
};

export default {
  components: {
    QueueBrowseModal,
    MyTicketsList,
    QueueConnectionBanner,
  },
  mixins: [alertMixin, queueSupervisionMixin],
  data() {
    return {
      waitingCounts: {},
      takingFrom: null,
      showBrowse: false,
      browseQueue: null,
      confirmMessage: '',
      recountTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      myQueues: 'queues/getMyQueues',
      uiFlags: 'queues/getUIFlags',
    }),
  },
  mounted() {
    if (!this.isQueueManagementEnabled || !this.canViewQueues) {
      return;
    }
    this.$store.dispatch('queues/get').then(() => this.refreshCounts());
    // QM §5.7 — the counts come from a per-queue endpoint, not the conversations store,
    // so nothing recomputes them when someone else takes or requeues a ticket.
    window.bus.$on(BUS_EVENTS.CONVERSATION_UPDATED, this.onConversationUpdated);
  },
  beforeDestroy() {
    window.bus.$off(
      BUS_EVENTS.CONVERSATION_UPDATED,
      this.onConversationUpdated
    );
    clearTimeout(this.recountTimer);
  },
  methods: {
    // QM §5.7 — only queue-managed conversations carry a routing_state, and only they can
    // move a waiting count. The whole set is recounted rather than the payload's queue
    // alone: a queue change alters the source queue's count too, and it is not in the event.
    onConversationUpdated(data) {
      if (!data?.routing_state) return;

      clearTimeout(this.recountTimer);
      this.recountTimer = setTimeout(
        () => this.refreshCounts(),
        RECOUNT_DEBOUNCE_MS
      );
    },
    waitingCount(queueId) {
      return this.waitingCounts[queueId] || 0;
    },
    async refreshCounts() {
      await Promise.all(
        this.myQueues.map(async queue => {
          try {
            // QM §5.10 — the list is paged now, so the count comes from meta;
            // payload.length would silently stop at one page.
            const { meta } = await this.$store.dispatch(
              'queues/getConversations',
              { queueId: queue.id }
            );
            this.$set(this.waitingCounts, queue.id, meta.total_count);
          } catch (error) {
            this.$set(this.waitingCounts, queue.id, 0);
          }
        })
      );
    },
    onTakeNext(queue) {
      this.takeNext(queue);
    },
    // QM §5.4.3 — the server picks the ticket, so it also reports every override that
    // applies to it (including "you recently returned this ticket"); one dialog lists them all.
    async takeNext(queue, confirmed = false) {
      this.takingFrom = queue.id;
      let flags = null;
      try {
        const { conversation_id: conversationId } = await this.$store.dispatch(
          'queues/takeNext',
          { queueId: queue.id, confirmed }
        );
        this.showAlert(
          this.$t('QUEUES_MGMT.AGENT.TOAST.TAKEN', { id: conversationId })
        );
        this.refreshCounts();
      } catch (error) {
        flags = error?.response?.data?.confirmation_required;
        if (!flags) {
          this.showAlert(this.pullErrorMessage(error));
          this.refreshCounts();
        }
      } finally {
        this.takingFrom = null;
      }
      if (flags && !confirmed && (await this.confirmOverride(flags))) {
        await this.takeNext(queue, true);
      }
    },
    confirmOverride(flags) {
      this.confirmMessage = flags
        .map(flag => this.$t(OVERRIDE_MESSAGES[flag]))
        .join(' ');
      return this.$refs.confirmDialog.showConfirmation();
    },
    pullErrorMessage(error) {
      const status = error?.response?.status;
      if (status === 404) return this.$t('QUEUES_MGMT.AGENT.TOAST.EMPTY');
      if (status === 409)
        return this.$t('QUEUES_MGMT.AGENT.TOAST.ALREADY_TAKEN');
      if (status === 403) return this.$t('QUEUES_MGMT.AGENT.TOAST.FORBIDDEN');
      return this.$t('QUEUES_MGMT.AGENT.TOAST.ERROR');
    },
    openBrowse(queue) {
      this.browseQueue = queue;
      this.showBrowse = true;
    },
    closeBrowse() {
      this.showBrowse = false;
      this.browseQueue = null;
    },
    onTicketTaken() {
      this.refreshCounts();
    },
  },
};
</script>
